import React, { useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import useVideoKeyboard from './useVideoKeyboard';

interface Options {
    url: string;
    thumbnailUrl?: string;
    onClose?: () => void;
}

const useVideoPlayer = ({ url, thumbnailUrl, onClose }: Options) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
    const hasStartedRef = useRef(false);
    const isEndedRef = useRef(false);
    const isFocusedRef = useRef(false);

    const [isEnded, setIsEnded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [quality, setQuality] = useState('Auto');

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }

        const videoEl = document.createElement('video');
        videoEl.className = 'video-js';
        videoRef.current.appendChild(videoEl);

        const player = videojs(videoEl, {
            controls: false,
            fluid: true,
            poster: thumbnailUrl || undefined,
            sources: [{ src: url, type: 'video/mp4' }],
            userActions: { click: false }
        });

        player.on('play', () => {
            hasStartedRef.current = true;
            isEndedRef.current = false;
            setIsPlaying(true);
            setIsEnded(false);
        });

        player.on('pause', () => setIsPlaying(false));

        player.on('ended', () => {
            isEndedRef.current = true;
            setIsPlaying(false);
            setIsEnded(true);
        });

        player.on('timeupdate', () => setCurrentTime(player.currentTime() ?? 0));

        player.on('loadedmetadata', () => setDuration(player.duration() ?? 0));

        player.on('volumechange', () => {
            setIsMuted(player.muted() ?? false);
            setVolume(player.volume() ?? 1);
        });

        playerRef.current = player;

        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            player.dispose();
            playerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.src([{ src: url, type: 'video/mp4' }]);
            playerRef.current.load();
            hasStartedRef.current = false;
            isEndedRef.current = false;
            setIsEnded(false);
            setCurrentTime(0);
            setIsPlaying(false);
            setDuration(0);
            setPlaybackSpeed(1);
            setQuality('Auto');
        }
    }, [url]);

    const handleFocus = () => {
        isFocusedRef.current = true;
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            isFocusedRef.current = false;
        }
    };

    const handlePlayPause = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.paused() ? p.play() : p.pause();
    };

    const handleSeek = (time: number) => {
        playerRef.current?.currentTime(time);
        setCurrentTime(time);
    };

    const handleToggleMute = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.muted(!p.muted());
    };

    const handleToggleFullscreen = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        if (document.fullscreenElement === containerRef.current) {
            document.exitFullscreen();
        } else if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen();
        }
    }, []);

    useVideoKeyboard({
        playerRef,
        hasStartedRef,
        isEndedRef,
        isFocusedRef,
        onToggleFullscreen: handleToggleFullscreen,
        onClose
    });

    const handleSpeedChange = (speed: number) => {
        playerRef.current?.playbackRate(speed);
        setPlaybackSpeed(speed);
        setShowSettings(false);
    };

    const handleQualityChange = (q: string) => {
        setQuality(q);
        setShowSettings(false);
    };

    const handleReplay = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        hasStartedRef.current = false;
        isEndedRef.current = false;
        setIsEnded(false);
        setCurrentTime(0);
        p.currentTime(0);
        p.play();
    };

    return {
        containerRef,
        videoRef,
        isEnded,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isFullscreen,
        showSettings,
        playbackSpeed,
        quality,
        handleFocus,
        handleBlur,
        handlePlayPause,
        handleSeek,
        handleToggleMute,
        handleToggleFullscreen,
        handleSpeedChange,
        handleQualityChange,
        handleReplay,
        toggleSettings: () => setShowSettings((v) => !v)
    };
};

export default useVideoPlayer;
